import { API_BASE_URL } from './env';
import { ApiError, type ApiErrorBody } from './errors';
import { tokenStore } from './token-store';

export type Page<T> = {
  data: T[];
  page: {
    limit: number;
    offset: number;
    count: number;
    hasMore: boolean;
    /**
     * Every row the filter matches, when the endpoint can say so cheaply.
     *
     * Optional, and absent on most endpoints: they page by fetching one row
     * past the page, which answers "is there more" without the COUNT that is
     * the expensive half of the query. Where it IS sent, the pager can say
     * which page of how many and how many rows there are in total.
     */
    total?: number;
  };
};

export type PageQuery = { limit?: number; offset?: number };

type QueryValue = string | number | boolean | null | undefined;

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Skips the bearer header and the refresh retry — used by the auth routes themselves. */
  anonymous?: boolean;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const envelope = body as Partial<ApiErrorBody> | null;
  const error = envelope?.error;
  return new ApiError(
    response.status,
    error?.code ?? `HTTP_${response.status}`,
    error?.message ?? response.statusText ?? 'Request failed',
    error?.details,
  );
}

/*
 * One refresh in flight at a time. Without this, a dashboard that fires six
 * queries at once would burn six refresh tokens on a single expiry — and since
 * the backend treats a re-presented token as theft and revokes the family, the
 * losers of that race would log the user out.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        tokenStore.clear();
        return false;
      }
      const payload = (await response.json()) as {
        data: { accessToken: string; refreshToken: string };
      };
      tokenStore.set(payload.data);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so every awaiter of this attempt sees one result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers = new Headers();

  /*
   * FormData passes through untouched, and deliberately WITHOUT a content-type.
   * Multipart needs a boundary parameter that only the browser can generate;
   * setting the header ourselves loses it and the server cannot parse the body.
   */
  const isMultipart = options.body instanceof FormData;
  if (options.body !== undefined && !isMultipart) {
    headers.set('content-type', 'application/json');
  }

  const token = tokenStore.getAccessToken();
  if (!options.anonymous && token) headers.set('authorization', `Bearer ${token}`);

  return fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body:
      options.body === undefined
        ? undefined
        : isMultipart
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  });
}

/**
 * Returns the raw response envelope. Every backend route answers with either
 * `{ data }`, `{ data, page }`, or 204 with no body at all.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshSession();
    if (refreshed) response = await send(path, options);
  }

  if (!response.ok) throw await toApiError(response);

  // 204 No Content — logout, role revoke, listing retire.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** For the `{ data: T }` envelope, which is most routes. */
export async function requestData<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const payload = await request<{ data: T }>(path, options);
  return payload.data;
}

/** For the `{ data: T[], page }` envelope produced by `toPage`. */
export function requestPage<T>(path: string, options: RequestOptions = {}): Promise<Page<T>> {
  return request<Page<T>>(path, options);
}

export const emptyPage = <T,>(limit = 20): Page<T> => ({
  data: [],
  page: { limit, offset: 0, count: 0, hasMore: false },
});
